<!--
  Created 23.05.2023
  Originated from Tailwind UI component: Forms, Select Menus, CustomWithAvatar
  https://tailwindui.com/components/application-ui/forms/select-menus#component-71d9116be789a254c260369f03472985
-->
<template>
  <Listbox
    v-model="selectedElement"
    :class="classes"
    as="div">
    <ListboxLabel
      v-if="label"
      class="block text-sm font-medium leading-6 text-gray-900">
      {{ label }}
    </ListboxLabel>
    <div
      class="relative"
      :class="{'mt-2': label}">
      <ListboxButton
        class="relative w-full rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900
          opacity-90 shadow-sm ring-1 ring-inset ring-gray-300 hover:opacity-100 focus:outline-none
          focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6">
        <span class="flex items-center">
          <component
            :is="selectedElement.icon"
            class="h-5 w-5 text-gray-400"
            aria-hidden="true" />
          <span class="ml-3 block truncate">{{ titleAlternative ? selectedElement.titleAlternative : selectedElement.title }}</span>
        </span>
        <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
          <span>
            <ChevronUpIcon
              class="h-4 w-4 text-gray-400"
              aria-hidden="true" />
            <ChevronDownIcon
              class="-mt-2 h-4 w-4 text-gray-400"
              aria-hidden="true" />
          </span>
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <ListboxOptions class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/50 focus:outline-none sm:text-sm">
          <ListboxOption
            v-for="element in elements"
            :key="element.id"
            v-slot="{ active, selected, disabled }"
            :disabled="element.disabled"
            as="template"
            :value="element">
            <li
              :class="[
                active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                'relative cursor-default select-none py-2 pl-3 pr-9',
                disabled ? 'cursor-not-allowed bg-gray-100 opacity-50' : '',
              ]">
              <div class="flex items-center">
                <component
                  :is="element.icon ?? UserGroupIcon"
                  class="h-5 w-5 text-gray-400"
                  aria-hidden="true" />
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate']">{{ titleAlternative ? element.titleAlternative : element.title }}</span>
              </div>

              <span
                v-if="selected"
                :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                <CheckIcon
                  class="h-5 w-5"
                  aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
          <button
            v-if="enableCreateOptions"
            :class="[elements.length === 0 ? '-my-1' : '-mb-1', 'flex w-full items-center border-t bg-blue-50 py-2 pl-3']"
            @click="$emit('createNewOption'); $event.target.blur()">
            <component
              :is="PlusIcon"
              class="h-5 w-5 text-gray-400"
              aria-hidden="true" />
            <span class="ml-3 block font-normal italic">{{ createOptionText }}</span>
          </button>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<script setup>
import {
  computed,
  ref, watch,
} from 'vue';
import {
  CheckIcon, ChevronUpIcon, ChevronDownIcon, XMarkIcon, UserGroupIcon, PlusIcon, ExclamationTriangleIcon,
} from '@heroicons/vue/24/solid';
import {
  Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions,
} from '@headlessui/vue';

const props = defineProps({
  classes: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  enableCreateOptions: {
    type: Boolean,
    required: false,
    default: false,
  },
  createOptionText: {
    type: String,
    required: false,
    default: 'Create a new option',
  },
  selectedElement: {
    type: Object,
    required: false,
    default: () => ({
      id: 1,
      title: 'No options exists',
      icon: XMarkIcon,
    }),
  },
  elements: {
    type: Array,
    required: true,
    default: () => [
      {
        id: 1,
        title: 'Not found',
        titleAlternative: 'No options exists',
        disabled: true,
        icon: ExclamationTriangleIcon,
      },
    ],
  },
  titleAlternative: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['createNewOption', 'update:selectedElement']);

const selectedElement = computed({
  get() {
    return props.selectedElement;
  },
  set(value) {
    emit('update:selectedElement', value);
  },
});

</script>
